import React, { useEffect, useState } from 'react';
import { BookOpen, Users, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { getTrainerCurriculum, listGroupEvents, bookGroupEvent } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const DISCIPLINE_LABELS = {
  jiujitsu: 'Brazilian Jiu-Jitsu',
  self_defense: 'Self-Defense',
  personal_training: 'Personal Training',
};

const BELT_DOT_STYLES = {
  white: { background: '#E8E8E8', border: '1px solid #ccc' },
  blue: { background: '#2563EB', border: '1px solid transparent' },
  purple: { background: '#7C3AED', border: '1px solid transparent' },
  brown: { background: '#92400E', border: '1px solid transparent' },
  black: { background: '#111827', border: '1px solid #444' },
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

export const TrainerCurriculumDisplay = ({ trainerId, offeredDisciplines }) => {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [activePhase, setActivePhase] = useState(0);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await getTrainerCurriculum(trainerId);
        if (cancel) return;
        setData(res.data);
        // pick first offered discipline that exists in the curriculum
        const keys = Object.keys(res.data?.disciplines || {});
        const first = (offeredDisciplines || []).find((d) => keys.includes(d)) || keys[0] || null;
        setActive(first);
      } catch (e) { /* silent */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [trainerId, offeredDisciplines]);

  if (loading) return null;
  const phases = data?.disciplines?.[active] || [];
  if (!phases.length) return null;

  // Only show disciplines the coach actually offers
  const tabs = Object.keys(data.disciplines || {}).filter((k) => !offeredDisciplines || offeredDisciplines.includes(k));
  const phase = phases[Math.min(activePhase, phases.length - 1)];

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="curriculum-display">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-[#C0A062]" />
          <h2 className="text-xl font-bold text-white">Curriculum</h2>
        </div>
        <button
          type="button"
          onClick={() => { setExpanded((v) => !v); setActivePhase(0); }}
          className="text-xs px-3 py-1.5 rounded-full border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
          data-testid="curriculum-toggle-btn"
        >
          {expanded ? 'Hide' : 'View curriculum'}
        </button>
      </div>

      {expanded && (
        <div className="mt-5" data-testid="curriculum-expanded">
          {tabs.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-5" data-testid="curriculum-display-tabs">
              {tabs.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => { setActive(k); setActivePhase(0); }}
                  className={`text-xs px-3 py-1.5 rounded-full border ${active === k ? 'bg-[#C0A062]/15 border-[#C0A062]/60 text-[#C0A062]' : 'border-zinc-700 text-zinc-400 hover:bg-zinc-800'}`}
                  data-testid={`curriculum-display-tab-${k}`}
                >
                  {DISCIPLINE_LABELS[k] || k}
                </button>
              ))}
            </div>
          )}

          {/* Belt timeline */}
          <div className="relative flex items-start justify-between mb-6 px-2" data-testid="belt-timeline">
            <div className="absolute left-4 right-4 top-[34px] h-px bg-zinc-700" aria-hidden="true" />
            {phases.map((p, i) => (
              <button
                key={p.id || i}
                type="button"
                onClick={() => setActivePhase(i)}
                className="relative z-10 flex flex-col items-center gap-1 group"
                data-testid={`belt-dot-${i}`}
              >
                <span className={`text-[10px] font-semibold ${activePhase === i ? 'text-[#C0A062]' : 'text-zinc-500'}`}>{i + 1}</span>
                <span
                  className="block w-4 h-4 rounded-full transition-transform group-hover:scale-110"
                  style={{
                    ...(BELT_DOT_STYLES[(p.belt || '').toLowerCase()] || BELT_DOT_STYLES.white),
                    outline: activePhase === i ? '2px solid #C0A062' : 'none',
                    outlineOffset: '2px',
                  }}
                />
                <span className={`text-[10px] capitalize ${activePhase === i ? 'text-white font-semibold' : 'text-zinc-500'}`}>{p.belt}</span>
              </button>
            ))}
          </div>

          {/* Active phase card */}
          <div className="bg-[#0d0d10] border border-zinc-800 rounded-md p-4" data-testid="active-phase-card">
            <p className="text-[10px] uppercase tracking-wider text-[#C0A062] font-semibold">Phase {activePhase + 1} · {cap(phase.belt)} Belt</p>
            <p className="text-white font-semibold mt-1">{phase.title}</p>
            {phase.tagline && <p className="text-sm italic mt-1" style={{ color: '#C0A062' }}>{phase.tagline}</p>}
            {phase.focus && <p className="text-zinc-300 text-sm mt-2">{phase.focus}</p>}
            {(phase.challenges || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="phase-skill-pills">
                {phase.challenges.slice(0, 6).map((c) => (
                  <span key={c.id} className="text-xs px-2.5 py-1 rounded-full bg-zinc-800/80 border border-zinc-700 text-zinc-300">
                    {c.description}
                  </span>
                ))}
              </div>
            )}
            {phase.outcome && <p className="text-xs text-zinc-500 mt-3">Outcome: {phase.outcome}</p>}
          </div>
        </div>
      )}
    </div>
  );
};

export const TrainerGroupEventsDisplay = ({ trainerId }) => {
  const { user, isClient } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickedEvent, setPickedEvent] = useState(null);
  const [liability, setLiability] = useState(false);
  const [booking, setBooking] = useState(false);

  const load = async () => {
    try {
      const res = await listGroupEvents(trainerId);
      setRows(res.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [trainerId]);

  if (loading || rows.length === 0) return null;

  const book = async () => {
    if (!liability) { toast.error('Please tick the liability acknowledgment.'); return; }
    if (!isClient) { toast.error('Sign in as a client to book.'); return; }
    setBooking(true);
    try {
      await bookGroupEvent(pickedEvent.id, true);
      toast.success('Spot reserved.');
      setPickedEvent(null);
      setLiability(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not book.');
    } finally { setBooking(false); }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="group-events-display">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-5 h-5 text-[#C0A062]" />
        <h2 className="text-xl font-bold text-white">Group sessions & seminars</h2>
      </div>
      <ul className="space-y-3">
        {rows.map((ev) => {
          const alreadyBooked = isClient && (ev.attendees || []).some((a) => a.client_id === user?.id);
          const full = ev.status === 'full' || (ev.attendees || []).length >= ev.capacity;
          return (
            <li key={ev.id} className="bg-[#0d0d10] border border-zinc-800 rounded-md p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`display-event-${ev.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-white font-semibold">{ev.title}</p>
                <p className="text-xs text-zinc-500 mt-1">
                  {ev.scheduled_date} @ {ev.scheduled_time} · {DISCIPLINE_LABELS[ev.category] || ev.category} · ${ev.price_per_person}/person · {ev.seats_remaining} seats left
                </p>
                {ev.description && <p className="text-sm text-zinc-400 mt-2">{ev.description}</p>}
                {ev.location && <p className="text-xs text-zinc-500 mt-1">{ev.location}</p>}
              </div>
              {alreadyBooked ? (
                <span className="text-emerald-300 text-sm flex items-center gap-1 self-center"><CheckCircle2 className="w-4 h-4" /> Booked</span>
              ) : full ? (
                <span className="text-amber-300 text-sm self-center">Full</span>
              ) : (
                <Button size="sm" className="bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold self-center" onClick={() => { setPickedEvent(ev); setLiability(false); }} data-testid={`display-book-${ev.id}`}>
                  Reserve spot
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <Dialog open={!!pickedEvent} onOpenChange={(o) => { if (!o) { setPickedEvent(null); setLiability(false); } }}>
        <DialogContent className="bg-[#0d0d10] border-zinc-800 text-white max-w-md" data-testid="group-book-dialog">
          <DialogHeader>
            <DialogTitle>Reserve spot</DialogTitle>
          </DialogHeader>
          {pickedEvent && (
            <div className="space-y-3">
              <p className="text-sm text-zinc-300">{pickedEvent.title}</p>
              <p className="text-xs text-zinc-500">{pickedEvent.scheduled_date} @ {pickedEvent.scheduled_time} · ${pickedEvent.price_per_person}/person</p>
              <label className="flex items-start gap-2 pt-2 cursor-pointer">
                <input type="checkbox" checked={liability} onChange={(e) => setLiability(e.target.checked)} className="mt-1 h-4 w-4 accent-[#C0A062]" data-testid="group-liability-checkbox" />
                <span className="text-xs text-zinc-300 leading-relaxed">I understand that combat sports and physical training carry inherent risks, and I accept responsibility for my participation in this session.</span>
              </label>
              <p className="text-[11px] text-amber-300/80">Payment collection for group events is currently <strong>MOCKED</strong> — your spot will be reserved but you won&apos;t be charged yet.</p>
              <Button onClick={book} disabled={!liability || booking} className="w-full bg-[#C0A062] text-black hover:bg-[#a98a52] font-semibold" data-testid="group-book-confirm">
                {booking && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Confirm reservation
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
